// src/screens/Admin/Metrics.js
import React, { useEffect, useState } from 'react';
import './Metrics.css';
import backIcon from '../../assets/backspace-icon.png';
import api from '../../api';
import { useNavigate } from 'react-router-dom';

// ✅ Format Pi amounts to 2 decimal places
const formatPi = (value) => {
  return value ? parseFloat(value).toFixed(2) : '0.00';
};

// ✅ Basic number helpers (safe + consistent UI)
const formatNum = (n) => {
  if (n === null || n === undefined) return '—';
  const x = Number(n);
  if (Number.isNaN(x)) return '—';
  return x.toLocaleString();
};

const formatDec = (n, d = 1) => {
  if (n === null || n === undefined) return '—';
  const x = Number(n);
  if (Number.isNaN(x)) return '—';
  return x.toFixed(d);
};

const formatPct = (n, d = 1) => {
  if (n === null || n === undefined) return '—';
  const x = Number(n);
  if (Number.isNaN(x)) return '—';
  return `${x.toFixed(d)}%`;
};

function Metrics() {
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('user');
  const navigate = useNavigate();

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const res = await api.get('/admin/metrics');
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch metrics', err);
    }
  };

  const renderStatCard = (label, value) => (
    <div className="metric-card">
      <div className="metric-card-title">{label}</div>
      <div className="metric-card-value">{value ?? '—'}</div>
    </div>
  );

  const renderListCard = (title, items, emptyText = '—') => (
    <div className="metric-card" style={{ gridColumn: '1 / -1' }}>
      <div className="metric-card-title">{title}</div>
      <div className="metric-card-value" style={{ fontSize: 14, lineHeight: 1.6 }}>
        {!items || items.length === 0 ? (
          emptyText
        ) : (
          items.map((line, idx) => (
            <div key={idx}>
              {idx + 1}. {line}
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderStats = () => {
    if (!data) return <div className="metrics-loading">Loading...</div>;

    switch (activeTab) {


      case 'user':
        return (
          <div className="metrics-grid">
            {renderStatCard('Total Users', data.totalUsers)}
            {renderStatCard('Total Pioneers', data.totalPioneers)}
            {renderStatCard('Total Apps', data.totalApps)}
            {renderStatCard('Active Apps', data.activeApps)}
            {renderStatCard('Verified Apps', data.verifiedApps)}
            {renderStatCard('Verified Active Apps', data.verifiedActiveApps)}
            {renderStatCard('Total Merchants', data.totalMerchants)}
            {renderStatCard('Total Admins', data.totalAdmins)}

            {renderStatCard('Active in Last 24h', data.activeInLast24h)}
            {renderStatCard('Active This Week', data.activeUsers)}
            {renderStatCard('New Signups (7 days)', data.newSignups7d)}
            {renderStatCard('New Signups (30 days)', data.newSignups30d)}
            {renderStatCard('Merchant Applications', data.merchantApplications)}
            {renderStatCard('Approved Merchants', data.approvedMerchants)}
            {renderStatCard('Active Merchants', data.paidMerchants)}
            {renderStatCard(
              'Active Merchant %',
              data.approvedMerchants > 0
                ? `${((data.paidMerchants / data.approvedMerchants) * 100).toFixed(1)}%`
                : '0%'
            )}
            {renderStatCard('Pending Merchants', data.pendingMerchants)}
            {renderStatCard('Suspended Merchants', data.suspendedMerchants)}
          </div>
        );


      case 'market':
        return (
          <div className="metrics-grid">
            {renderStatCard('Top Category', data.topCategory)}
            {renderStatCard('Total Products', data.totalProducts)}
            {renderStatCard('Pending Products', data.pendingEscrowProducts)}
            {renderStatCard('Products Sold', data.productsSold)}
            {renderStatCard('Products This Week', data.productsAddedThisWeek)}
            {renderStatCard('Cancelled Products', data.cancelledProducts)}
          </div>
        );

      case 'pi':
        return (
          <div className="metrics-grid">
            {renderStatCard('Total Pi in Escrow', `${formatPi(data.totalEscrowPi)}π`)}
            {renderStatCard('Pi Released', `${formatPi(data.piReleased)}π`)}
            {renderStatCard(
              'Tips Sent',
              `${data.tipsSentCount} tips / ${formatPi(data.totalTipsPi)}π`
            )}
            {renderStatCard('Pending Orders', data.totalPendingOrders)}
            {renderStatCard('Cancelled Orders', data.totalCancelledOrders)}
            {renderStatCard('Completed Orders', data.totalCompletedOrders)}
          </div>
        );

      case 'social': {
        const s = data.postsSocial7d;

        // ✅ Always show lifetime totals (even if s exists)
        const lifetimeTotals = (
          <>
            {renderStatCard('Total Posts (All-time)', formatNum(data.totalPosts))}
            {renderStatCard('Total Likes (All-time)', formatNum(data.totalLikes))}
            {renderStatCard('Total Comments (All-time)', formatNum(data.totalComments))}
          </>
        );

        // Fallback if backend not deployed yet
        if (!s) {
          return (
            <div className="metrics-grid">
              {lifetimeTotals}
              {renderStatCard('Reports (7 days)', formatNum(data.reportsLast7Days))}
            </div>
          );
        }

        const topPostersLines = (s.topPosters || []).map(
          (u) => `${u.username} — ${formatNum(u.posts)} posts`
        );

        const topEngagedLines = (s.topEngagedUsers || []).map((u) => {
          const avgPerPost =
            u.avgInteractionsPerPost !== undefined
              ? `${formatDec(u.avgInteractionsPerPost, 1)}/post`
              : '—';
          return `${u.username} — ${formatNum(u.interactions)} interactions (${avgPerPost})`;
        });

        return (
          <div className="metrics-grid">
            {/* ✅ Context totals first */}
            {lifetimeTotals}

            {/* ✅ Moderation context (must be above 7d reports section per your request) */}
            {renderStatCard('Reports (7 days)', formatNum(data.reportsLast7Days))}

            {/* 7d Core health */}
            {renderStatCard('Posts (7d)', formatNum(s.posts))}
            {renderStatCard('Avg Posts / Day (7d)', formatDec(s.avgPostsPerDay, 1))}
            {renderStatCard('Unique Posters (7d)', formatNum(s.uniquePosters))}
            {renderStatCard('Avg Posts / Poster (7d)', formatDec(s.avgPostsPerPoster, 2))}

            {/* 7d Engagement quality */}
            {renderStatCard('Avg Likes / Post (7d)', formatDec(s.avgLikesPerPost, 2))}
            {renderStatCard('Avg Comments / Post (7d)', formatDec(s.avgCommentsPerPost, 2))}

            {/* 7d Distribution / control */}
            {renderStatCard('Top 10 Poster Share (7d)', formatPct(s.top10PosterSharePct, 1))}

            {/* 7d New user adoption */}
            {renderStatCard('New Users Who Posted (7d)', formatNum(s.newUsersWhoPosted))}
            {renderStatCard('New User Post Rate (7d)', formatPct(s.newUserPostRatePct, 1))}

            {/* Lists */}
            {renderListCard('Top Posters (7d)', topPostersLines)}
            {renderListCard('Top Engaged Users (7d)', topEngagedLines)}
          </div>
        );
      }

      case 'revenue':
        return (
          <div className="metrics-grid">
            {renderStatCard('Merchant Subscriptions', `${formatPi(data.revenueSubscriptions)}π`)}
            {renderStatCard('Sponsored Products', `${formatPi(data.revenueSponsorships)}π`)}
            {renderStatCard(
              'Escrow Commission (10%)',
              `${formatPi(data.revenueEscrowCommission)}π`
            )}
            {renderStatCard('Tips Commission (5%)', `${formatPi(data.revenueTipCommission)}π`)}
            {renderStatCard('Total Revenue', `${formatPi(data.totalRevenue)}π`)}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="metrics-container">
      <div className="metrics-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="back-arrow"
          onClick={() => navigate('/admin')}
        />
        <span className="metrics-title">Metrics</span>
      </div>

      <div className="metrics-filter">
        <button
          className={activeTab === 'user' ? 'active' : ''}
          onClick={() => setActiveTab('user')}
        >
          🧑‍🤝‍🧑 User Stats
        </button>
        <button
          className={activeTab === 'market' ? 'active' : ''}
          onClick={() => setActiveTab('market')}
        >
          📦 Marketplace
        </button>
        <button className={activeTab === 'pi' ? 'active' : ''} onClick={() => setActiveTab('pi')}>
          🪙 Pi Flow
        </button>
        <button
          className={activeTab === 'social' ? 'active' : ''}
          onClick={() => setActiveTab('social')}
        >
          📬 Posts & Social
        </button>
        <button
          className={activeTab === 'revenue' ? 'active' : ''}
          onClick={() => setActiveTab('revenue')}
        >
          💰 Revenue
        </button>
      </div>

      <div className="metrics-content">{renderStats()}</div>
    </div>
  );
}

export default Metrics;
